import type { FC, ReactNode, RefObject } from 'react';

import { Drawer, IconButton, Typography } from '@mui/material';
import { ChevronDownDashboardIcon, CloseIcon } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useKeyboardFit } from 'shared/lib/screen/useKeyboardFit';

type Props = {
  open: boolean;
  title: string;
  /**
   * Accessible name for the close control. The sheet is already named by its title, so the default
   * is enough wherever that title says what is being closed — name it only when the title is
   * generic, as "Actions" is.
   */
  closeLabel?: string;
  /** Drop the close control where dismissing should go through an action instead */
  withCloseIcon?: boolean;
  onClose: VoidFunction;
  /** Turns the sheet into a sub-screen: the left slot navigates back instead of staying empty */
  onBack?: VoidFunction;
  /** Buttons pinned under the body, clear of the home indicator. Pass them bare and in the order a
   * dialog would lay them out left to right — the sheet stacks and stretches them itself, and
   * reverses them so the commit action a desktop puts rightmost ends up on top. */
  footer?: ReactNode;
  /** The scroll root, for a body that pages in more items as it nears the bottom */
  bodyRef?: RefObject<HTMLDivElement>;
  /** For a body that lays out and scrolls its own regions — a chat panel, say — rather than being a
   * column of content the sheet pads and scrolls for it */
  flush?: boolean;
  /** Paper classes, normally a height the default cap is too generous for */
  className?: string;
  children: ReactNode;
};

/** The panel that rises from the bottom edge where a desktop would open a popover or a dialog. */
export const BottomSheet: FC<Props> = ({
  open,
  title,
  closeLabel = 'Close',
  withCloseIcon = true,
  onClose,
  onBack,
  footer,
  bodyRef,
  flush = false,
  className,
  children,
}) => {
  const paperRef = useKeyboardFit();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      PaperProps={{
        ref: paperRef,
        role: 'dialog',
        'aria-label': title,
        className: clsx('max-h-[90dvh] overflow-hidden rounded-t-2xl', className),
      }}
      onClose={onClose}
    >
      {/* Equal side tracks keep the title optically centered, as in MobileHeader. The controls pin
          to the top of the row rather than its middle: both are 32px against a 28px line, so nothing
          moves under a one-line title, and a title that wraps grows downward past them. */}
      <div className="grid shrink-0 grid-cols-[minmax(min-content,1fr)_auto_minmax(min-content,1fr)] items-center gap-2 px-4 pt-4 pb-2">
        <div className="flex min-h-8 items-center self-start justify-self-start">
          {onBack && (
            <button
              type="button"
              className="-ml-1 flex cursor-pointer items-center bg-transparent text-primary-main"
              onClick={onBack}
            >
              <ChevronDownDashboardIcon className="rotate-90" />
              <Typography variant="body1">Back</Typography>
            </button>
          )}
        </div>
        {/* `text-center` only shows once a title wraps: a single-line one already sits in a track
            exactly as wide as itself */}
        <Typography className="text-center" component="h2" variant="h2">
          {title}
        </Typography>
        {withCloseIcon && (
          <IconButton
            aria-label={closeLabel}
            className="h-8 w-8 self-start justify-self-end bg-grey20 p-0 text-grey70 hover:bg-grey30"
            onClick={onClose}
          >
            <CloseIcon height={20} width={20} />
          </IconButton>
        )}
      </div>

      {/* The footer's own 12px is a gap between controls, not breathing room for the body — without
          this the last line of content sits almost against the first button, and a sheet with no
          footer ends flush against the bottom edge. */}
      <div
        ref={bodyRef}
        className={clsx(
          'min-h-0 flex-1',
          // A flush body ends at the sheet's edge, so the home indicator is its own to clear.
          flush ? 'flex flex-col overflow-hidden pb-[env(safe-area-inset-bottom)]' : 'overflow-y-auto px-4 pb-4',
        )}
      >
        {children}
      </div>

      {!!footer && (
        <div className="flex shrink-0 flex-col-reverse gap-2 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] [&>*]:w-full">
          {footer}
        </div>
      )}
    </Drawer>
  );
};
